import { z } from 'zod';

/**
 * Avis clients (CDC §16) : l'administrateur (ou un superviseur autorisé) sélectionne des clients dont l'installation
 * ou l'entretien est terminé et leur envoie une demande d'avis avec le lien Google Business du territoire (sinon
 * celui de l'entreprise). Aucune note préalable n'est demandée (pas de filtrage des avis). Chaque envoi est historisé ;
 * un second envoi au même client est refusé sauf confirmation explicite (doublons involontaires).
 */
export const REVIEW_CHANNELS = ['sms', 'whatsapp', 'email'] as const;
export type ReviewChannel = (typeof REVIEW_CHANNELS)[number];
export const REVIEW_CHANNEL_LABELS: Record<ReviewChannel, string> = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  email: 'E-mail',
};

export const DEFAULT_REVIEW_MESSAGE =
  'Bonjour {prenom}, merci d’avoir fait confiance à {entreprise} pour votre climatisation ! Votre avis compte beaucoup pour nous : {lien}';

export interface ReviewRequestEntry {
  id: string;
  channel: ReviewChannel;
  at: string;
  byName: string;
}

export interface ReviewCandidate {
  dossierId: string;
  reference: string;
  territoryId: string;
  typeLabel: string;
  clientId: string;
  clientName: string;
  firstName: string | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  completedAt: string;
  technicianName: string | null;
  /** requests already sent to this client (any dossier), most recent first */
  requests: ReviewRequestEntry[];
  /** Google link that will be sent (territory, else company); null = not configured */
  reviewUrl: string | null;
}

export const reviewRequestInput = z.object({
  dossierId: z.string().uuid(),
  channel: z.enum(REVIEW_CHANNELS),
  /** explicit confirmation to send again to a client already asked */
  force: z.boolean().default(false),
  /** envoi direct par le service d'envoi (SMS / e-mail) au lieu d'ouvrir l'application de l'utilisateur */
  auto: z.boolean().default(false),
});

/** Envoi automatique en lot (SMS ou e-mail). `force` = relance assumée des clients déjà sollicités. */
export const reviewBatchInput = z.object({
  dossierIds: z.array(z.string().uuid()).min(1).max(200),
  channel: z.enum(['sms', 'email']),
  force: z.boolean().default(false),
});
export interface ReviewBatchResult {
  dossierId: string;
  ok: boolean;
  error?: string;
}

export const reviewSettingsInput = z.object({
  googleReviewUrl: z.string().trim().url('Lien invalide').max(500).nullable(),
  message: z
    .string()
    .trim()
    .min(10)
    .max(600)
    .refine((v) => v.includes('{lien}'), 'Le message doit contenir {lien}'),
});

export function renderReviewMessage(
  template: string,
  v: { prenom: string | null; lien: string; entreprise: string },
) {
  return template
    .replace(/\{prenom\}/g, v.prenom?.trim() || '')
    .replace(/\{lien\}/g, v.lien)
    .replace(/\{entreprise\}/g, v.entreprise)
    .replace(/\s+,/g, ',')
    .replace(/Bonjour ,/, 'Bonjour,');
}
